import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListAssetLibraryResult } from '@whinerp/client';
import type { Block } from '@whinerp/contracts/blocks';
import type { BlockLibrary } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * The asset library as the block editors see it (F-084): the picker opens
 * inline in the email and page builders. Undefined when the workspace does not
 * have the library, so the editors look exactly as they did without it.
 */
export function useBlockLibrary(): BlockLibrary | undefined {
  const t = useT();
  const { enabled } = useEntitlement('F-084');
  const library = useQuery<ListAssetLibraryResult, ApiError>({
    queryKey: ['asset-library', 'editor'],
    queryFn: () => api.listAssetLibrary({ query: {} }),
    enabled,
    retry: false,
  });
  if (!enabled || !library.data) return undefined;
  const assets = library.data.assets;
  return {
    label: t('assets.fromLibrary'),
    chooseImage: t('assets.chooseImage'),
    images: assets.filter((asset) => asset.kind === 'image' && asset.url).map((asset) => ({ name: asset.name, url: asset.url ?? '' })),
    snippets: assets.filter((asset) => asset.kind === 'snippet').map((asset) => ({ name: asset.name, body: asset.body ?? '' })),
    templates: assets
      .filter((asset) => asset.kind === 'template')
      .map((asset) => ({ name: asset.name, blocks: (asset.blocks ?? []) as Block[] })),
  };
}
